import React, { useEffect, useState } from 'react';
import { EYEBROW } from './liStyles';
import { titleCase } from '../shared/format';

// Closed & new — information, never a task: closed places are already left out of
// every answer. Per work-page-fixes Item 1, the old collapsible panel is gone. In
// its place: a slim full-width button ("Closed right now · N places") that opens a
// right-hand slide-out holding the closed and new-on-map lists. No data shows on the
// button — only the label and the combined count. MiniList is the slide-out body
// (label + rows + "See all N"); it carries its own horizontal padding so the eyebrow,
// the rows, and the link all align to the slide-out's edges.
const MiniList = ({ label, block, drill, emptyText, onOpenDrill }) => {
  const rows = Array.isArray(block?.rows) ? block.rows.slice(0, 5) : [];
  const total = block?.total || 0;
  return (
    <div>
      <div className={`${EYEBROW} px-4 sm:px-6 pt-1 pb-2`}>{label}</div>
      {block?.status === 'no_data' || rows.length === 0 ? (
        <p className="px-4 sm:px-6 pb-3 text-sm text-gray-500 dark:text-gray-400">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((r) => (
            <li key={r.entity_id}>
              <button
                type="button"
                className="w-full text-left px-4 sm:px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50"
                onClick={() => onOpenDrill(r.drill)}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.name}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{r.since_label ? `${r.label || ''} ${r.since_label}`.trim() : r.label}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {drill && total > rows.length && (
        <div className="px-4 sm:px-6 py-2">
          <button type="button" className="text-sm font-medium text-[#32B1FF] hover:underline" onClick={() => onOpenDrill(drill)}>See all {total}</button>
        </div>
      )}
    </div>
  );
};

const ClosedAndNew = ({ panel, onOpenDrill, variant = 'card' }) => {
  const [open, setOpen] = useState(false);
  const facts = panel?.facts || {};
  const closed = facts.closed || {};
  const newOnMap = facts.new_on_map || {};
  // N = closed.total + new_on_map.total — the combined count shown on the button.
  const total = (closed.total || 0) + (newOnMap.total || 0);
  const title = titleCase(panel?.title || 'Closed right now');

  // Escape closes the slide-out (matches DrillOver on this page). Optional per the
  // tracker, but cheap and consistent with the page's other slide-out.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // The trigger button: variant="footer" is a slim strip with a top divider that
  // embeds inside the Places Open Right Now card (no own card border); the default
  // "card" variant is its own bordered card, used when the changes panel is salient
  // and renders standalone in the attention slot.
  const triggerClass = variant === 'footer'
    ? 'w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50'
    : 'w-full flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 sm:px-5 py-3 text-left shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50';

  return (
    <section id="panel-changes" aria-label={title}>
      {/* Slim full-width button — one row tall. Label + combined count on the left,
          a "View" label + chevron-right on the right. No closed/new data on the
          button. Clicking opens the slide-out below. */}
      <button
        type="button"
        data-testid="closed-right-now-trigger"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={triggerClass}
      >
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {title}
          <span className="text-gray-500 dark:text-gray-400 font-normal"> · {total} {total === 1 ? 'place' : 'places'}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-[#32B1FF] shrink-0">
          View
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50" role="presentation">
          {/* Dimmed overlay — clicking it closes the slide-out. */}
          <button
            type="button"
            data-testid="closed-right-now-scrim"
            aria-label={`Close ${title}`}
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px] cursor-default"
          />
          {/* Slide-out panel — right side, ~440px, full viewport height. Mirrors
              DrillOver's styling (bg-white dark:bg-gray-900, shadow-2xl). On phone it
              is a full-width sheet; sm+ slides in from the right. */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            data-testid="closed-right-now-panel"
            className="absolute bg-white dark:bg-gray-900 shadow-2xl flex flex-col outline-none inset-y-0 left-0 right-0 sm:left-auto sm:right-0 sm:w-[440px]"
          >
            {/* Header — title (+ optional sentence) and a close × button. */}
            <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</h2>
                {panel?.sentence && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{panel.sentence}</p>}
              </div>
              <button
                type="button"
                data-testid="closed-right-now-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="p-1.5 -mr-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {/* Body — closed list first, new-on-map list below it. */}
            <div className="flex-1 overflow-y-auto py-4 space-y-6">
              <MiniList label={titleCase(facts.closed_label || 'Closed right now')} block={closed} drill={facts.closed_drill} emptyText="None within a 45-minute drive." onOpenDrill={onOpenDrill} />
              <MiniList label={titleCase(facts.new_on_map_label || 'New on the map')} block={newOnMap} drill={facts.new_on_map_drill} emptyText="New places show after the next area scan." onOpenDrill={onOpenDrill} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ClosedAndNew;
